/**
 * Cache wrapper for the unscheduled-jobs backlog (`/get-unscheduled`). The endpoint POSTs an empty
 * body and is scoped entirely server-side by the auth/impersonation context — it is NOT scoped to
 * the dashboard's `?company=` selector (the backlog spans every company the scheduler can see). So
 * the cache keys by the impersonation context: a developer impersonating a user gets that user's
 * backlog under a distinct key, while their own backlog stays under "self".
 *
 * TtlCache (15-min, in-flight coalescing) means the two enrichment passes / a StrictMode double-mount
 * collapse to one fetch, and an in-app remount is instant. Mutations that change the backlog (saving
 * shift assignments) must call `clear()` — `Scheduling.vm` does so in `savePendingShifts`.
 */

import { Job } from "@domain/CRM/job";
import { TtlCache } from "../../../cache/ttlCache";
import { API } from "../../../api";
import { getImpersonatedEmail } from "../../../storage/impersonationStore";

const BACKLOG_TTL_MS = 15 * 60 * 1000;
const SELF_KEY = "self" as const;

class CachedUnscheduledJobs {
	private cache = new TtlCache<string, Job[]>(BACKLOG_TTL_MS, () => API.Data.Jobs.get.unscheduled());

	/** Cache key = the active impersonation context (the only axis the backend scopes the backlog by). */
	private key(): string {
		return getImpersonatedEmail() ?? SELF_KEY;
	}

	/** Synchronous TTL cache hit for the current context, or null if missing/expired. */
	public peek(): Job[] | null {
		return this.cache.peek(this.key());
	}

	public get(): Promise<Job[]> {
		return this.cache.get(this.key());
	}

	/** Drops every context's entry — call after a mutation that changes the backlog. */
	public clear(): void {
		this.cache.clear();
	}
}

export class CachedJobsAPI {
	public unscheduled = new CachedUnscheduledJobs();
}
